import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useThreadStore } from "@/stores/thread-store";
import { useChatStore } from "@/stores/chat-store";
import { useThreadList, useThreadActions } from "@/hooks/useThreads";
import { getThread } from "@/services/thread-service";
import type { ChatThread } from "@/types/thread";
import {
  MessageSquare,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  conceptId: string;
}

export const ThreadSidebar = memo(function ThreadSidebar({ conceptId }: Props) {
  const { activeThreadId, setActiveThreadId, sidebarOpen, toggleSidebar } =
    useThreadStore();
  const { data: threads, isError: threadsFailed, refetch: refetchThreads } = useThreadList(conceptId);
  const { renameThread, removeThread, clearAll } =
    useThreadActions(conceptId);
  const [confirmClear, setConfirmClear] = useState(false);

  const handleNewChat = useCallback(() => {
    setActiveThreadId(null);
    useChatStore.getState().clearChat();
  }, [setActiveThreadId]);

  const handleSelectThread = useCallback(
    async (threadId: string) => {
      if (threadId === activeThreadId) return;
      setActiveThreadId(threadId);
      try {
        const detail = await getThread(conceptId, threadId);
        // Only load if this thread is still the active one (user may have clicked another)
        if (useThreadStore.getState().activeThreadId === threadId) {
          useChatStore.getState().loadMessages(
            detail.messages.map((m) => ({
              id: m.id,
              role: m.role as "user" | "assistant",
              content: m.content,
              sources: m.sources,
            })),
          );
        }
      } catch {
        // Thread load failed — leave chat empty
      }
    },
    [activeThreadId, setActiveThreadId, conceptId],
  );

  if (!sidebarOpen) {
    return (
      <div className="flex w-8 shrink-0 flex-col items-center border-r py-2">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={toggleSidebar}
          aria-label="Open thread sidebar"
        >
          <PanelLeftOpen className="size-3.5" />
        </Button>
        {threads && threads.length > 0 && (
          <span className="mt-1 text-[10px] text-muted-foreground">
            {threads.length}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-60 shrink-0 flex-col border-r">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-xs font-medium">Threads</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={handleNewChat}
            aria-label="New chat"
          >
            <Plus className="size-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={toggleSidebar}
            aria-label="Close thread sidebar"
          >
            <PanelLeftClose className="size-3" />
          </Button>
        </div>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {threadsFailed && !threads && (
          <div className="px-3 py-6 text-center">
            <p className="mb-2 text-xs text-muted-foreground">
              Couldn't load your conversations.
            </p>
            <Button variant="outline" size="xs" onClick={() => void refetchThreads()}>
              Try again
            </Button>
          </div>
        )}

        {!(threadsFailed && !threads) && (!threads || threads.length === 0) && (
          <div className="px-3 py-6 text-center">
            <MessageSquare className="mx-auto mb-2 size-5 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              No conversations yet
            </p>
          </div>
        )}

        {threads?.map((thread) => (
          <ThreadItem
            key={thread.id}
            thread={thread}
            isActive={thread.id === activeThreadId}
            onSelect={handleSelectThread}
            onRename={(id, title) =>
              renameThread.mutate({ threadId: id, title })
            }
            onDelete={(id) => removeThread.mutate(id)}
          />
        ))}
      </div>

      {/* Clear all */}
      {threads && threads.length > 0 && (
        <div className="border-t px-3 py-2">
          {confirmClear ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-destructive">Clear all?</span>
              <div className="flex gap-1">
                <Button
                  variant="destructive"
                  size="xs"
                  onClick={() => {
                    clearAll.mutate();
                    setConfirmClear(false);
                  }}
                >
                  Yes
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setConfirmClear(false)}
                >
                  No
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="xs"
              className="w-full text-xs text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmClear(true)}
            >
              <Trash2 className="mr-1 size-3" />
              Clear all threads
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

interface ThreadItemProps {
  thread: ChatThread;
  isActive: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}

function ThreadItem({
  thread,
  isActive,
  onSelect,
  onRename,
  onDelete,
}: ThreadItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(thread.title);
  const [showMenu, setShowMenu] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const handleDoubleClick = useCallback(() => {
    setEditValue(thread.title);
    setIsEditing(true);
  }, [thread.title]);

  const handleRenameSubmit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== thread.title) {
      onRename(thread.id, trimmed);
    }
    setIsEditing(false);
  }, [editValue, thread.id, thread.title, onRename]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleRenameSubmit();
      } else if (e.key === "Escape") {
        setIsEditing(false);
      }
    },
    [handleRenameSubmit],
  );

  const relativeTime = formatRelativeTime(thread.updated_at);

  return (
    <div
      className={cn(
        "group relative flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-accent/50",
        isActive && "bg-accent",
      )}
      onClick={() => onSelect(thread.id)}
      onDoubleClick={handleDoubleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect(thread.id);
      }}
    >
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            className="w-full rounded border bg-background px-1 text-xs outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <p className="truncate text-xs font-medium">{thread.title}</p>
        )}
        <p className="text-[10px] text-muted-foreground">
          {relativeTime}
          {thread.message_count > 0 && (
            <span className="ml-1.5">
              {thread.message_count} msg{thread.message_count !== 1 ? "s" : ""}
            </span>
          )}
        </p>
      </div>

      {/* Actions menu */}
      <div className="relative shrink-0 opacity-0 group-hover:opacity-100">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu((prev) => !prev);
          }}
          aria-label="Thread actions"
        >
          <MoreHorizontal className="size-3" />
        </Button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
              }}
            />
            <div className="absolute right-0 top-6 z-20 w-32 rounded-md border bg-popover py-1 shadow-md">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  setEditValue(thread.title);
                  setIsEditing(true);
                }}
              >
                <Pencil className="size-3" />
                Rename
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onDelete(thread.id);
                }}
              >
                <Trash2 className="size-3" />
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
